import { createReducer, on } from '@ngrx/store';
import { closingNoteActions } from '../actions/closing-note.actions';
import { IClosingNote, NoteKindWire } from '../services/closing-note.service';

export const CLOSING_NOTE_FEATURE_KEY = 'abcpClosingNotes';

export interface ClosingNoteState {
    projectId: string | null;
    /** Die Liste je Art. Getrennt gehalten, weil die beiden Register nie gemeinsam gelesen werden. */
    byKind: Record<NoteKindWire, IClosingNote[]>;
    /** Die Kommentare EINES Kontos — eigener Eimer, damit der Bereich das Register nicht ueberschreibt. */
    accountNotes: IClosingNote[];
    /** The Pendenzen of the same account — the account page's Pendenzen area. */
    accountTodos: IClosingNote[];
    accountId: string | null;
    /** Own account id: the comment load sets `accountId` first and must not hide a switch of account. */
    accountTodosAccountId: string | null;
    /**
     * Die Kommentare EINES Belegs — REV-21369, der Beleg-Viewer (ADR-0021 E6). Eigener Eimer aus
     * demselben Grund wie der des Kontos: ein offener Beleg darf weder das Register noch den
     * Konto-Bereich überschreiben.
     */
    documentNotes: IClosingNote[];
    documentId: string | null;

    loading: Record<NoteKindWire, boolean>;
    error: Record<NoteKindWire, boolean>;
    accountLoading: boolean;
    accountError: boolean;
    accountTodosLoading: boolean;
    accountTodosError: boolean;
    documentLoading: boolean;
    documentError: boolean;

    detail: IClosingNote | null;
    detailLoading: boolean;
    detailError: boolean;
    /** ⚠️ Getrennt von `detailError` — „gibt es nicht" ist kein Systemfehler (AC9). */
    detailMissing: boolean;

    commentFilter: 'all' | 'internal' | 'closingMeeting';
    todoStatus: 'open' | 'done';
    todoAudience: 'all' | 'todo' | 'reviewer' | 'client';
    sortKey: string | null;
    sortDirection: 'asc' | 'desc';
    removedFilters: string[];
    searchTerm: string;

    expandedNoteIds: string[];
    editingNoteId: string | null;
    composerKind: NoteKindWire | null;
    recentlyChangedId: string | null;
    writeError: string | null;
}

export const initialClosingNoteState: ClosingNoteState = {
    projectId: null,
    byKind: { COMMENT: [], TODO: [] },
    accountNotes: [],
    accountTodos: [],
    accountId: null,
    accountTodosAccountId: null,
    documentNotes: [],
    documentId: null,
    loading: { COMMENT: false, TODO: false },
    error: { COMMENT: false, TODO: false },
    accountLoading: false,
    accountError: false,
    accountTodosLoading: false,
    accountTodosError: false,
    documentLoading: false,
    documentError: false,
    detail: null,
    detailLoading: false,
    detailError: false,
    detailMissing: false,
    commentFilter: 'all',
    todoStatus: 'open',
    todoAudience: 'all',
    sortKey: null,
    sortDirection: 'asc',
    removedFilters: [],
    searchTerm: '',
    expandedNoteIds: [],
    editingNoteId: null,
    composerKind: null,
    recentlyChangedId: null,
    writeError: null,
};

/**
 * Ersetzt einen Eintrag in allen Eimern, in denen er liegt.
 *
 * ⚠️ Ein Eintrag kann in ZWEI Listen stehen: im Register seiner Art und, wenn er an einem Konto
 * haengt, im Konto-Bereich. Nur einen zu aktualisieren hiesse, dass dieselbe Notiz an zwei Stellen
 * Verschiedenes sagt — und der Nutzer sieht beide gleichzeitig nebeneinander.
 */
/**
 * REV-22490 — puts a just-created note into a list BY ITS ID: replaced where it already is, appended
 * otherwise. A list that was loading while the note was created can already hold it: the server
 * answered the GET after storing the note but before the POST came back. Appending blindly showed the
 * note twice (seen on Testing, `notes-orchestrator:173`).
 */
function upsert(list: IClosingNote[], note: IClosingNote): IClosingNote[] {
    return list.some((n) => n._id === note._id) ? list.map((n) => (n._id === note._id ? note : n)) : [...list, note];
}

function replaceEverywhere(state: ClosingNoteState, note: IClosingNote): ClosingNoteState {
    const swap = (list: IClosingNote[]) => list.map((n) => (n._id === note._id ? note : n));
    return {
        ...state,
        byKind: { ...state.byKind, [note.kind]: swap(state.byKind[note.kind]) },
        accountNotes: swap(state.accountNotes),
        accountTodos: swap(state.accountTodos),
        documentNotes: swap(state.documentNotes),
        detail: state.detail && state.detail._id === note._id ? note : state.detail,
    };
}

function removeEverywhere(state: ClosingNoteState, noteId: string): ClosingNoteState {
    const drop = (list: IClosingNote[]) => list.filter((n) => n._id !== noteId);
    return {
        ...state,
        byKind: { COMMENT: drop(state.byKind.COMMENT), TODO: drop(state.byKind.TODO) },
        accountNotes: drop(state.accountNotes),
        accountTodos: drop(state.accountTodos),
        documentNotes: drop(state.documentNotes),
        detail: state.detail && state.detail._id === noteId ? null : state.detail,
    };
}

export const closingNoteReducer = createReducer(
    initialClosingNoteState,

    on(closingNoteActions.loadNotes, (state, { projectId, kind, accountId, documentId }) => {
        if (documentId !== undefined) {
            // Ein ANDERER Beleg leert den Eimer sofort: sonst stünden die Markierungen des
            // vorigen Belegs auf dem neuen, bis die Antwort da ist.
            const same = state.documentId === documentId;
            // ⚠️ Kommentare und Pendenzen kommen in ZWEI Antworten (`loadForDocument`). „Lädt" folgt
            // den Kommentaren — die sind die Liste; die Pendenzen liefern nur Markierungen nach.
            return {
                ...state,
                projectId,
                documentId,
                documentNotes: same ? state.documentNotes : [],
                documentLoading: kind === 'COMMENT' ? true : state.documentLoading,
                documentError: same ? state.documentError && kind !== 'COMMENT' : false,
            };
        }
        if (accountId === undefined) {
            return {
                ...state,
                projectId,
                loading: { ...state.loading, [kind]: true },
                error: { ...state.error, [kind]: false },
            };
        }
        // Another account empties the Pendenzen at once, so the previous account's never show here.
        return kind === 'TODO'
            ? {
                  ...state,
                  projectId,
                  accountTodosAccountId: accountId,
                  accountTodos: state.accountTodosAccountId === accountId ? state.accountTodos : [],
                  accountTodosLoading: true,
                  accountTodosError: false,
              }
            : { ...state, projectId, accountId, accountLoading: true, accountError: false };
    }),

    on(closingNoteActions.loadNotesSuccess, (state, { projectId, kind, accountId, documentId, notes }) => {
        // ⚠️ Eine spaet eintreffende Antwort eines ANDEREN Abschlusses wird verworfen. Sie
        // anzuzeigen hiesse, die Notizen eines fremden Mandats auf diesem Abschluss zu zeigen.
        if (state.projectId !== null && state.projectId !== projectId) {
            return state;
        }
        if (documentId !== undefined) {
            // Dasselbe für einen inzwischen verlassenen Beleg.
            // ⚠️ Nur die Art DIESER Antwort wird ersetzt — sonst löschte die Pendenzen-Antwort die
            // Kommentare (und umgekehrt), je nachdem, welche zuletzt ankommt.
            return documentId === state.documentId
                ? {
                      ...state,
                      documentNotes: [...state.documentNotes.filter((n) => n.kind !== kind), ...notes],
                      documentLoading: kind === 'COMMENT' ? false : state.documentLoading,
                  }
                : state;
        }
        if (accountId === undefined) {
            return {
                ...state,
                byKind: { ...state.byKind, [kind]: notes },
                loading: { ...state.loading, [kind]: false },
            };
        }
        if (kind === 'TODO') {
            return accountId === state.accountTodosAccountId
                ? { ...state, accountTodos: notes, accountTodosLoading: false }
                : state;
        }
        return accountId === state.accountId ? { ...state, accountNotes: notes, accountLoading: false } : state;
    }),

    on(closingNoteActions.loadNotesFailure, (state, { kind, accountId, documentId }) => {
        if (documentId !== undefined) {
            return {
                ...state,
                documentLoading: kind === 'COMMENT' ? false : state.documentLoading,
                documentError: state.documentError || documentId === state.documentId,
            };
        }
        if (accountId !== undefined) {
            if (kind === 'TODO') {
                return accountId === state.accountTodosAccountId
                    ? { ...state, accountTodosLoading: false, accountTodosError: true }
                    : state;
            }
            return { ...state, accountLoading: false, accountError: true };
        }
        return { ...state, loading: { ...state.loading, [kind]: false }, error: { ...state.error, [kind]: true } };
    }),

    on(closingNoteActions.loadNote, (state) => ({
        ...state,
        detailLoading: true,
        detailError: false,
        detailMissing: false,
    })),
    on(closingNoteActions.loadNoteSuccess, (state, { note }) => ({
        ...state,
        detail: note,
        detailLoading: false,
        detailError: false,
        detailMissing: false,
    })),
    on(closingNoteActions.loadNoteFailure, (state, { notFound }) => ({
        ...state,
        detail: null,
        detailLoading: false,
        // Genau EINER der beiden ist gesetzt — siehe AC9.
        detailMissing: notFound,
        detailError: !notFound,
    })),

    on(closingNoteActions.createNoteSuccess, (state, { note }) => ({
        ...state,
        byKind: { ...state.byKind, [note.kind]: upsert(state.byKind[note.kind], note) },
        // ⚠️ Nur dann in den Konto-Eimer, wenn es ein KOMMENTAR dieses Kontos ist: der Bereich
        // listet keine Pendenzen (ADR-0019 E10).
        accountNotes:
            note.kind === 'COMMENT' && note.accountId !== null && note.accountId === state.accountId
                ? upsert(state.accountNotes, note)
                : state.accountNotes,
        accountTodos:
            note.kind === 'TODO' && note.accountId !== null && note.accountId === state.accountTodosAccountId
                ? upsert(state.accountTodos, note)
                : state.accountTodos,
        // REV-21369 — in den Beleg-Eimer jede Notiz DIESES Belegs, auch eine Pendenz: ihre Markierung
        // wird gezeichnet (R2). Gelistet werden am Beleg nur Kommentare — das filtert die Seite.
        documentNotes:
            !!note.documentId && note.documentId === state.documentId
                ? upsert(state.documentNotes, note)
                : state.documentNotes,
        composerKind: null,
        writeError: null,
    })),
    on(closingNoteActions.createNoteFailure, (state, { error }) => ({ ...state, writeError: error })),

    on(closingNoteActions.updateNoteSuccess, (state, { note }) => ({
        ...replaceEverywhere(state, note),
        editingNoteId: null,
        writeError: null,
    })),
    on(closingNoteActions.updateNoteFailure, (state, { error }) => ({ ...state, writeError: error })),

    on(closingNoteActions.deleteNoteSuccess, (state, { noteId }) => removeEverywhere(state, noteId)),
    on(closingNoteActions.deleteNoteFailure, (state, { error }) => ({ ...state, writeError: error })),

    on(closingNoteActions.setNoteDoneSuccess, (state, { note }) => ({
        ...replaceEverywhere(state, note),
        // AC8 — die Zeile bleibt stehen und laesst sich zuruecknehmen.
        recentlyChangedId: note._id,
    })),
    on(closingNoteActions.setNoteDoneFailure, (state, { error }) => ({ ...state, writeError: error })),
    on(closingNoteActions.clearRecentlyChanged, (state) => ({ ...state, recentlyChangedId: null })),

    on(closingNoteActions.replyToNoteSuccess, (state, { note }) => replaceEverywhere(state, note)),
    on(closingNoteActions.replyToNoteFailure, (state, { error }) => ({ ...state, writeError: error })),

    on(closingNoteActions.setCommentFilter, (state, { filter }) => ({ ...state, commentFilter: filter })),
    on(closingNoteActions.setTodoStatus, (state, { status }) => ({ ...state, todoStatus: status })),
    on(closingNoteActions.setTodoAudience, (state, { audience }) => ({ ...state, todoAudience: audience })),
    on(closingNoteActions.setSort, (state, { key, direction }) => ({
        ...state,
        sortKey: key,
        sortDirection: direction,
    })),
    on(closingNoteActions.removeFilter, (state, { key }) => ({
        ...state,
        // Der Filter, den der Nutzer abgeworfen hat, geht auf seinen Ausgangswert zurueck.
        commentFilter: key === 'tags' ? 'all' : state.commentFilter,
        todoAudience: key === 'tags' ? 'all' : state.todoAudience,
        removedFilters: [...state.removedFilters, key],
    })),
    on(closingNoteActions.resetFilters, (state) => ({
        ...state,
        commentFilter: 'all' as const,
        todoAudience: 'all' as const,
        todoStatus: 'open' as const,
        searchTerm: '',
        sortKey: null,
        removedFilters: [],
    })),
    on(closingNoteActions.setSearchTerm, (state, { term }) => ({ ...state, searchTerm: term })),

    on(closingNoteActions.toggleThread, (state, { noteId }) => ({
        ...state,
        expandedNoteIds:
            state.expandedNoteIds.indexOf(noteId) === -1
                ? [...state.expandedNoteIds, noteId]
                : state.expandedNoteIds.filter((id) => id !== noteId),
    })),
    on(closingNoteActions.startEdit, (state, { noteId }) => ({ ...state, editingNoteId: noteId })),
    on(closingNoteActions.openComposer, (state, { kind }) => ({ ...state, composerKind: kind })),
);
